/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { ShopperBasketsV2 } from '@/scapi';
import type { PaymentData } from '@/lib/checkout/schemas';
import { CHECKOUT_STEPS, type CheckoutStep, type CustomerProfile } from './checkout-context-types';
import type { ShipmentDistribution } from './checkout-distribution';

const normalizeAddressField = (value: string | undefined) => (value ?? '').trim().toLowerCase();
const normalizePostalForCompare = (value: string | undefined) => (value ?? '').trim().split('-')[0].toLowerCase();

function isSameAddressForAvs(
    a:
        | {
              address1?: string;
              city?: string;
              stateCode?: string;
              postalCode?: string;
              countryCode?: string;
          }
        | undefined,
    b:
        | {
              address1?: string;
              city?: string;
              stateCode?: string;
              postalCode?: string;
              countryCode?: string;
          }
        | undefined
) {
    if (!a || !b) return false;
    return (
        normalizeAddressField(a.address1) === normalizeAddressField(b.address1) &&
        normalizeAddressField(a.city) === normalizeAddressField(b.city) &&
        normalizeAddressField(a.stateCode) === normalizeAddressField(b.stateCode) &&
        normalizePostalForCompare(a.postalCode) === normalizePostalForCompare(b.postalCode) &&
        normalizeAddressField(a.countryCode) === normalizeAddressField(b.countryCode)
    );
}

/**
 * Determines whether the user's current payment form selection differs from the payment
 * instrument already on the basket. When they differ, payment must be re-submitted before
 * place order so the basket reflects the user's actual choice (e.g. switching from a saved
 * card to a new card, or choosing a different saved card).
 */
export function doesPaymentSelectionDiffer(
    paymentData: PaymentData,
    basket: ShopperBasketsV2.schemas['Basket'] | null | undefined
): boolean {
    const basketInstrument = basket?.paymentInstruments?.[0];
    if (!basketInstrument) return false;

    // PayPal express: the instrument was attached by the PayPal approve bridge (with the approved
    // PayPal order id). The payment form defaults to a credit-card selection when nothing else is
    // chosen, so we can't just always return "differs" — that would replace PayPal with an empty
    // card on Place Order.
    //
    // We rely on `paymentType === 'paypal'` to positively identify "shopper's current selection is
    // still PayPal" (`use-payment.ts` `formDataGetter` stamps this when `selectedPaymentMethod`
    // holds `'paypal'`). Any OTHER selection — new card via Converge LightBox, saved card, Acima,
    // Ashley Advantage — must be treated as a real switch (SSF-443): return `true` so
    // submit-payment runs and its `clearBasketPaymentInstruments` removes the PayPal instrument
    // before attaching the new tender. Prior behavior of unconditionally returning `false` here
    // silently dropped the PayPal → Credit Card switch, leaving the payment summary and BM order
    // recorded as PayPal.
    if (basketInstrument.paymentMethodId === 'PayPal') {
        if (paymentData.paymentType !== 'paypal') {
            return true;
        }

        // Shopper stayed on PayPal. Still treat it as changed when billing-address intent/values
        // differ so submit-payment runs and persists the billing address before review/place-order.
        const basketShipping = basket?.shipments?.[0]?.shippingAddress;
        const basketBilling = basket?.billingAddress;
        if (paymentData.billingSameAsShipping) {
            if (basketShipping && !isSameAddressForAvs(basketBilling, basketShipping)) {
                return true;
            }
        } else {
            const formBilling = {
                address1: paymentData.billingAddress1,
                city: paymentData.billingCity,
                stateCode: paymentData.billingStateCode,
                postalCode: paymentData.billingPostalCode,
                countryCode: paymentData.billingCountryCode || 'US',
            };
            if (!isSameAddressForAvs(basketBilling, formBilling)) {
                return true;
            }
        }

        return false;
    }

    // Acima lease-to-own: the basket already carries an ACIMA_FINANCE instrument (added by a prior
    // submit-payment during the lease flow).
    //   - Still on Acima (paymentData is ACIMA_FINANCE): the instrument matches the selection, so
    //     it does NOT differ — place directly. Without this, Acima (which carries
    //     useSavedPaymentMethod=false) would always be treated as "differs", needlessly re-running
    //     submit-payment; and if the local Acima selection state was lost (e.g. a loader
    //     revalidation after a failed place-order auth) the re-run would fall through to the
    //     credit-card schema and reject with card-field errors instead of placing the order.
    //   - Switched away (paymentData is a card / Ashley Advantage): it DOES differ — return true so
    //     place-order re-runs submit-payment, which clears the stale ACIMA_FINANCE instrument (and
    //     its lease) before attaching the new tender. Otherwise the new order keeps the Acima
    //     instrument and the Acima afterPOST hook re-authorizes the already-used lease
    //     ("Lease already associated with <prior order>").
    if (basketInstrument.paymentMethodId === 'ACIMA_FINANCE') {
        return paymentData.paymentMethodId !== 'ACIMA_FINANCE';
    }

    // User chose "enter a new card" — the basket's existing instrument (from a prior saved-card
    // auto-apply or a previous payment submit) is stale and must be replaced.
    if (!paymentData.useSavedPaymentMethod) {
        return true;
    }

    // User chose a saved card. The basket instrument doesn't store the customerPaymentInstrumentId
    // (v2 basket API limitation), so we can't reliably detect whether the user picked a different
    // saved card than what's already on the basket. Always re-submit to guarantee the correct card
    // is applied — the server action is idempotent (removes old instrument, adds the selected one).
    if (paymentData.useSavedPaymentMethod && paymentData.selectedSavedPaymentMethod) {
        return true;
    }

    // Even when tender is unchanged, billing-address edits must re-run submit-payment so the
    // basket billing address used by place-order / AVS is current.
    const basketShipping = basket?.shipments?.[0]?.shippingAddress;
    const basketBilling = basket?.billingAddress;
    if (paymentData.billingSameAsShipping) {
        if (basketShipping && !isSameAddressForAvs(basketBilling, basketShipping)) {
            return true;
        }
    } else {
        const formBilling = {
            address1: paymentData.billingAddress1,
            city: paymentData.billingCity,
            stateCode: paymentData.billingStateCode,
            postalCode: paymentData.billingPostalCode,
            countryCode: paymentData.billingCountryCode || 'US',
        };
        if (!isSameAddressForAvs(basketBilling, formBilling)) {
            return true;
        }
    }

    return false;
}

function getCustomStringValue(source: unknown, key: string): string | undefined {
    if (!source || typeof source !== 'object') {
        return undefined;
    }
    const value = (source as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : undefined;
}

function getCustomBooleanValue(source: unknown, key: string): boolean {
    if (!source || typeof source !== 'object') {
        return false;
    }
    const value = (source as Record<string, unknown>)[key];
    return value === true;
}

export function hasHDItemsInBasket(basket: ShopperBasketsV2.schemas['Basket'] | undefined): boolean {
    if (!basket?.productItems?.length) {
        return false;
    }

    return basket.productItems.some((item) => {
        const deliveryMethod = getCustomStringValue(item, 'c_deliveryMethod');
        return deliveryMethod === 'HD';
    });
}

export function shouldShowDeliveryScheduleStep(basket: ShopperBasketsV2.schemas['Basket'] | undefined): boolean {
    if (!basket) {
        return false;
    }

    const isOHDSAvailable = getCustomBooleanValue(basket, 'c_ohdsAvailable');
    return isOHDSAvailable && hasHDItemsInBasket(basket);
}

function hasValidPaymentCard(
    paymentInstrument: ShopperBasketsV2.schemas['OrderPaymentInstrument'] | undefined
): boolean {
    if (!paymentInstrument || paymentInstrument.paymentMethodId !== 'CREDIT_CARD') {
        return false;
    }

    // For saved payment methods, check if paymentInstrumentId exists
    if (paymentInstrument.paymentInstrumentId) {
        return true;
    }

    // For new payment methods, check if all required card fields are present
    const card = paymentInstrument.paymentCard;
    return !!(card?.cardType && card?.expirationMonth && card?.expirationYear && card?.maskedNumber);
}

/**
 * Payment methods whose mere presence on the basket means the shopper has finished choosing a
 * tender — there are no card fields to validate. PayPal is attached out-of-band by the express
 * approval bridge (action.paypal-set-payment); ACIMA_FINANCE / ASHLEY_FINANCE are attached by the
 * payment-step submit. CHECKOUT-14 added an intermediate order-review step gated on a "complete"
 * payment, and `hasValidPaymentCard` only recognises CREDIT_CARD — so without this set the flow
 * would stay pinned on PAYMENT for these tenders and the shopper could never reach Place Order.
 */
const COMPLETED_TENDER_METHOD_IDS = new Set(['PayPal', 'ACIMA_FINANCE', 'ASHLEY_FINANCE']);

/**
 * Whether the basket's payment instrument represents a completed payment choice: either a valid
 * credit card or an attached alternative tender (PayPal / Acima / Ashley Advantage).
 */
function hasCompletedPayment(
    paymentInstrument: ShopperBasketsV2.schemas['OrderPaymentInstrument'] | undefined
): boolean {
    if (!paymentInstrument) {
        return false;
    }
    if (hasValidPaymentCard(paymentInstrument)) {
        return true;
    }
    if (COMPLETED_TENDER_METHOD_IDS.has(paymentInstrument.paymentMethodId ?? '')) {
        return true;
    }
    // Converge LightBox card: the card is captured in Converge's hosted iframe, so the basket
    // instrument carries the LightBox markers (and card brand) but NO maskedNumber until the
    // place-order APG authorization runs — meaning hasValidPaymentCard would never pass. Treat the
    // presence of the LightBox markers on a CREDIT_CARD instrument as a completed card choice.
    const lightBox = paymentInstrument as unknown as { c_isLightBox?: boolean; c_sslToken?: string };
    return (
        paymentInstrument.paymentMethodId === 'CREDIT_CARD' &&
        (lightBox.c_isLightBox === true || Boolean(lightBox.c_sslToken))
    );
}

export function computeFinalStepForReturningCustomer(
    basket: ShopperBasketsV2.schemas['Basket'] | undefined,
    customerProfile: CustomerProfile,
    shipmentDistribution: ShipmentDistribution
): CheckoutStep | null {
    if (!customerProfile?.customer || !basket) {
        return null;
    }

    // For returning registered customers, determine step based on customer profile data
    // since the basket will be prefilled during checkout initialization
    const hasCustomerEmail = customerProfile.customer.login;
    const hasCustomerAddresses = customerProfile.addresses && customerProfile.addresses.length > 0;
    const hasCustomerPaymentMethods =
        customerProfile.paymentInstruments && customerProfile.paymentInstruments.length > 0;
    // allow checkout even if payment section complete even without SPM
    const paymentInstrument = basket.paymentInstruments?.[0];
    const paymentValid = paymentInstrument && hasCompletedPayment(paymentInstrument);

    if (hasCustomerEmail && hasCustomerAddresses) {
        // SSF-552 follow-up: When the basket has delivery items but no shipping address
        // yet, pin returning customers to SHIPPING_ADDRESS — the same guard
        // `computeStepFromBasket` applies for guests. This closes the case where a
        // shopper signs in mid-checkout (e.g. via the LoginModal from an expanded
        // Contact Info step) and the `BasketProvider` still holds the pre-login guest
        // basket that has no address. The seeder in `_checkout.checkout.tsx` is
        // intentionally single-shot per `basketId` (to protect fetcher-driven writes
        // for HD/THD, combine deliveries, etc.), so the loader-prefilled basket does
        // not reach `useBasket()` on the same-basket revalidation triggered by login.
        // Without this guard, the fall-through path returns PAYMENT (when the customer
        // has no saved payment) or PLACE_ORDER — collapsing every earlier step and
        // hiding the address form behind Payment.
        if (shipmentDistribution.hasUnaddressedDeliveryItems) {
            return CHECKOUT_STEPS.SHIPPING_ADDRESS;
        }

        // INT-25: When the Experian validation hook flagged the latest shipping address as
        // needing shopper confirmation, keep returning customers pinned to SHIPPING_ADDRESS too.
        // Mirrors the guard in computeStepFromBasket so the basket-driven auto-advance effect
        // in CheckoutProvider does not collapse the step behind the address validation modal.
        if (getCustomBooleanValue(basket, 'c_experianValidationPending')) {
            return CHECKOUT_STEPS.SHIPPING_ADDRESS;
        }

        // INT-25: Force the shipping-method step for returning customers too when the shopper
        // has not yet confirmed a method and multiple applicable methods exist (the submit-
        // shipping-address action only auto-confirms when there is exactly one applicable
        // method). The existing ShippingOptions component auto-submits when the basket already
        // has a method assigned, so this is a guarded landing — not a forced extra click.
        if (
            !shipmentDistribution.hasUnaddressedDeliveryItems &&
            !getCustomBooleanValue(basket, 'c_shippingMethodConfirmed')
        ) {
            return CHECKOUT_STEPS.SHIPPING_OPTIONS;
        }

        // INT-12 (Sprint N+1): the standalone delivery-schedule step is gone; OHDS
        // is now an inline picker on each HD method tile and never advances the
        // step order. Returning customers go straight from method to payment.
        if (hasCustomerPaymentMethods || paymentValid) {
            return CHECKOUT_STEPS.PLACE_ORDER;
        }

        return CHECKOUT_STEPS.PAYMENT;
    }

    // If customer has email but no addresses, go to shipping address (unless basket already has one)
    if (hasCustomerEmail && !hasCustomerAddresses) {
        return shipmentDistribution.hasUnaddressedDeliveryItems ? CHECKOUT_STEPS.SHIPPING_ADDRESS : null;
    }

    // If customer has no email (shouldn't happen for registered users), go to contact info
    if (!hasCustomerEmail) {
        return CHECKOUT_STEPS.CONTACT_INFO;
    }

    // Fallback to review if we can't determine the step
    return CHECKOUT_STEPS.PLACE_ORDER;
}

/**
 * Handle navigation to appropriate next checkout step from Pickup.
 * - If there are delivery items + pickup, advance to shipping address.
 * - If no delivery items (pickup only), advance to payment.
 *
 * @param isPickup - basket has at least one pickup shipment
 * @param hasDeliveryItems - basket has at least one delivery shipment/items
 * @param goToStep - callback for advancing step
 * @param STEPS - checkout steps
 * @param t - translation
 * @returns { label, onClick }
 */
export function handlePickupContinueAction(
    isPickup: boolean,
    hasDeliveryItems: boolean,
    goToStep: (step: CheckoutStep) => void,
    STEPS: typeof CHECKOUT_STEPS,
    t: (key: string) => string
): { label: string; onClick: () => void } {
    if (isPickup && hasDeliveryItems) {
        return {
            label: t('checkout.pickUp.continueToShipping'),
            onClick: () => goToStep(STEPS.SHIPPING_ADDRESS as CheckoutStep),
        };
    } else {
        return {
            label: t('checkout.pickUp.continueToPayment'),
            onClick: () => goToStep(STEPS.PAYMENT as CheckoutStep),
        };
    }
}

/**
 * Computes the furthest step the basket qualifies for based on its current state.
 *
 * For **guest** checkout this acts as a ceiling — `exitEditMode` advances sequentially
 * and never jumps past this value. For **returning customers** it drives the initial
 * landing step (with auto-submit handling in ShippingOptions for pre-applied methods).
 *
 * INT-12 (Sprint N+1): the standalone delivery-schedule step was deleted. The
 * OHDS date picker now lives inline on each HD method tile and never advances
 * the step order — so the basket flows straight from SHIPPING_OPTIONS to PAYMENT
 * even on OHDS-eligible ZIPs.
 */
export function computeStepFromBasket(
    basket: ShopperBasketsV2.schemas['Basket'] | undefined,
    shipmentDistribution: ShipmentDistribution
): CheckoutStep {
    if (!basket) {
        return CHECKOUT_STEPS.CONTACT_INFO;
    }

    if (!basket.customerInfo?.email) {
        return CHECKOUT_STEPS.CONTACT_INFO;
    }

    if (shipmentDistribution.hasDeliveryItems) {
        if (shipmentDistribution.hasUnaddressedDeliveryItems) {
            return CHECKOUT_STEPS.SHIPPING_ADDRESS;
        }

        // INT-25: When the Experian validation hook flagged the latest shipping address as
        // needing shopper confirmation (suggested / multiple matches / no match), keep the
        // computed step pinned to SHIPPING_ADDRESS so the basket-driven auto-advance effect
        // in CheckoutProvider does not collapse the step behind the address validation modal.
        // Cleared by the hook on the resubmit that follows the shopper's modal choice.
        if (getCustomBooleanValue(basket, 'c_experianValidationPending')) {
            return CHECKOUT_STEPS.SHIPPING_ADDRESS;
        }

        if (shipmentDistribution.needsShippingMethods) {
            return CHECKOUT_STEPS.SHIPPING_OPTIONS;
        }

        // INT-25: SCAPI's updateShippingAddressForShipment auto-assigns a default shipping
        // method server-side, so basket.shipments[0].shippingMethod alone does not prove the
        // shopper actively picked one. When multiple methods are applicable the submit-shipping-
        // address action leaves c_shippingMethodConfirmed=false; we keep the computed step at
        // SHIPPING_OPTIONS until the shopper submits that step. With a single applicable method
        // the action sets c_shippingMethodConfirmed=true, so the step is auto-skipped (no real
        // choice to make).
        if (!getCustomBooleanValue(basket, 'c_shippingMethodConfirmed')) {
            return CHECKOUT_STEPS.SHIPPING_OPTIONS;
        }
    }

    const paymentInstrument = basket.paymentInstruments?.[0];
    if (!paymentInstrument || !hasCompletedPayment(paymentInstrument)) {
        return CHECKOUT_STEPS.PAYMENT;
    }

    return CHECKOUT_STEPS.PLACE_ORDER;
}

export function getCompletedSteps(
    basket: ShopperBasketsV2.schemas['Basket'] | undefined,
    shipmentDistribution: ShipmentDistribution,
    currentStep: CheckoutStep
): CheckoutStep[] {
    const completed: CheckoutStep[] = [];

    if (!basket) {
        return completed;
    }

    const hasEmail =
        basket.customerInfo?.email ||
        (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('checkoutEmail'));
    if (hasEmail && currentStep > CHECKOUT_STEPS.CONTACT_INFO) {
        completed.push(CHECKOUT_STEPS.CONTACT_INFO);
    }

    if (shipmentDistribution.hasDeliveryItems) {
        if (!shipmentDistribution.hasUnaddressedDeliveryItems && currentStep > CHECKOUT_STEPS.SHIPPING_ADDRESS) {
            completed.push(CHECKOUT_STEPS.SHIPPING_ADDRESS);
        }

        if (!shipmentDistribution.needsShippingMethods && currentStep > CHECKOUT_STEPS.SHIPPING_OPTIONS) {
            completed.push(CHECKOUT_STEPS.SHIPPING_OPTIONS);
        }
    }

    const paymentInstrument = basket.paymentInstruments?.[0];
    if (paymentInstrument && hasCompletedPayment(paymentInstrument) && currentStep > CHECKOUT_STEPS.PAYMENT) {
        completed.push(CHECKOUT_STEPS.PAYMENT);
    }

    return completed;
}

/**
 * Returns true when the shipping-methods map contains at least one valid, selectable method
 * across all shipments. A method is valid when it has an id, a name, and a numeric price.
 * Used to block advancing past the Shipping Address step when the submitted address yields
 * no deliverable options.
 */
export function hasAnyValidShippingMethod(
    map: Record<string, ShopperBasketsV2.schemas['ShippingMethodResult']> | undefined
): boolean {
    if (!map) return false;
    for (const result of Object.values(map)) {
        const methods = result?.applicableShippingMethods;
        if (
            methods &&
            methods.length > 0 &&
            methods.some((m) => m.id && m.name && typeof m.price === 'number' && !Number.isNaN(m.price))
        ) {
            return true;
        }
    }
    return false;
}

export function shouldAutoAdvanceForReturningCustomer(
    isRegisteredCustomer: boolean,
    customerProfile?: CustomerProfile
): boolean {
    if (!isRegisteredCustomer || !customerProfile) {
        return false;
    }

    const hasAddresses = customerProfile.addresses && customerProfile.addresses.length > 0;
    const hasPaymentMethods = customerProfile.paymentInstruments && customerProfile.paymentInstruments.length > 0;

    return hasAddresses && hasPaymentMethods;
}
